import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PortalUser, PracticeInfo } from '../types/portal';

interface PortalAuthState {
  portalUser: PortalUser | null;
  isAuthenticated: boolean;
  token: string | null;
  practiceInfo: PracticeInfo | null;
  _hasHydrated: boolean;

  // Actions
  setUser: (user: PortalUser | null) => void;
  login: (user: PortalUser, token: string, practiceInfo: PracticeInfo) => void;
  logout: () => void;
  updateToken: (token: string) => void;
  setPracticeInfo: (practiceInfo: PracticeInfo) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

/** Ensure PII fields are always strings (guards against encrypted objects from stale cache) */
function sanitizePortalUser(user: PortalUser | null): PortalUser | null {
  if (!user) {
    return null;
  }
  return {
    ...user,
    firstName: typeof user.firstName === 'string' ? user.firstName : '',
    lastName: typeof user.lastName === 'string' ? user.lastName : '',
    email: typeof user.email === 'string' ? user.email : '',
    displayName:
      user.displayName === null || user.displayName === undefined
        ? (user.displayName ?? null)
        : typeof user.displayName === 'string'
          ? user.displayName
          : null,
    avatarUrl:
      user.avatarUrl === null || user.avatarUrl === undefined
        ? (user.avatarUrl ?? null)
        : typeof user.avatarUrl === 'string'
          ? user.avatarUrl
          : null,
  };
}

export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    set => ({
      portalUser: null,
      isAuthenticated: false,
      token: null,
      practiceInfo: null,
      _hasHydrated: false,

      setUser: portalUser =>
        set({
          portalUser: sanitizePortalUser(portalUser),
          isAuthenticated: !!portalUser,
        }),

      login: (portalUser, token, practiceInfo) =>
        set({
          portalUser: sanitizePortalUser(portalUser),
          token,
          practiceInfo,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          portalUser: null,
          token: null,
          isAuthenticated: false,
          practiceInfo: null,
        }),

      updateToken: token => set({ token }),

      setPracticeInfo: practiceInfo => set({ practiceInfo }),

      setHasHydrated: hasHydrated => set({ _hasHydrated: hasHydrated }),
    }),
    {
      name: 'portal-auth',
      onRehydrateStorage: () => state => {
        // Sanitize portalUser on rehydration to fix stale/corrupted localStorage data
        if (state?.portalUser) {
          state.setUser(state.portalUser);
        }
        state?.setHasHydrated(true);
      },
      partialize: state => ({
        portalUser: state.portalUser,
        isAuthenticated: state.isAuthenticated,
        // token is intentionally excluded from persistence to prevent XSS token theft
        // from localStorage. Token lives only in memory (Zustand state).
        practiceInfo: state.practiceInfo,
      }),
    }
  )
);
